import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminClient } from "@/lib/api";

const JOB_KEY = "lbp-enhancer-active-job";
const RESULT_KEY = "lbp-enhancer-last-result";
const savedResult = () => {
    try { return JSON.parse(sessionStorage.getItem(RESULT_KEY) || "null"); }
    catch { return null; }
};

export const useVideoEnhancement = () => {
    const [jobId, setJobId] = useState(() => sessionStorage.getItem(JOB_KEY));
    const [uploading, setUploading] = useState(false);
    const [polling, setPolling] = useState(false);
    const [phase, setPhase] = useState("");
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(savedResult);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);
    const uploadLock = useRef(false);

    useEffect(() => {
        if (!jobId) return;
        let stopped = false;
        let timer;
        let missing = 0;
        const started = Date.now();
        setPolling(true);
        setError("");
        setPhase("Checking enhancement progress…");
        const poll = async () => {
            try {
                const { data } = await adminClient().get(`/admin/enhance-video/${jobId}/status`, { timeout: 15000 });
                if (stopped) return;
                missing = 0;
                if (data.status === "done" || data.status === "failed") {
                    setPolling(false);
                    setPhase("");
                    sessionStorage.removeItem(JOB_KEY);
                    setJobId(null);
                    if (data.status === "done") {
                        setResult(data);
                        sessionStorage.setItem(RESULT_KEY, JSON.stringify(data));
                        toast.success("Enhanced video ready.");
                    } else {
                        setError(data.error || "Enhancement failed. Try a shorter clip or turn off motion smoothing.");
                    }
                    return;
                }
                setPhase(data.phase || "Enhancing footage");
                setProgress(Number.isFinite(data.progress) ? data.progress : null);
            } catch (e) {
                if (stopped) return;
                if ([401, 403].includes(e?.response?.status)) {
                    setError("Your admin session ended. Sign in again to check this enhancement.");
                    setPolling(false);
                    return;
                }
                if (e?.response?.status === 404) missing += 1;
                setPhase("Reconnecting to your enhancement…");
            }
            if (stopped) return;
            if (missing >= 20 || Date.now() - started > 60 * 60 * 1000) {
                setPolling(false);
                setError("Progress is temporarily unavailable. Check status again before starting another enhancement.");
                return;
            }
            timer = setTimeout(poll, 3000);
        };
        poll();
        return () => { stopped = true; clearTimeout(timer); };
    }, [jobId, retry]);

    const enhance = async (file, strength, smoothMotion) => {
        if (uploadLock.current || jobId || !file) return;
        uploadLock.current = true;
        setUploading(true);
        setResult(null);
        sessionStorage.removeItem(RESULT_KEY);
        setError("");
        setProgress(null);
        setPhase("Uploading original footage…");
        const form = new FormData();
        form.append("video", file);
        form.append("strength", strength);
        form.append("smooth_motion", String(smoothMotion));
        try {
            const { data } = await adminClient().post("/admin/enhance-video", form, {
                timeout: 10 * 60 * 1000,
                onUploadProgress: ({ loaded, total }) => {
                    if (total) setPhase(loaded >= total ? "Securing original footage…" : `Uploading original footage · ${Math.round(loaded / total * 100)}%`);
                },
            });
            if (!data.id) throw new Error("No enhancement ID returned.");
            sessionStorage.setItem(JOB_KEY, data.id);
            setJobId(data.id);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            setError(status === 413 ? "Upload too large. Choose a video under 80MB."
                : typeof detail === "string" ? detail
                : e.message === "Network Error" ? "Upload interrupted. Check your connection and try a smaller video."
                : e.message || "Upload failed. Please try again.");
            setPhase("");
        } finally {
            setUploading(false);
            uploadLock.current = false;
        }
    };
    return { busy: uploading || polling || !!jobId, polling, jobId, phase, progress, result, error,
        enhance, checkStatus: () => setRetry(value => value + 1),
        reset: () => { setResult(null); sessionStorage.removeItem(RESULT_KEY); setError(""); setProgress(null); } };
};